import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Space,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { agentService, Agent, CreateAgentRequest, UpdateAgentRequest } from '../services/agentService';
import { employeeService, Employee } from '../services/employeeService';
import TimeDisplay from './TimeDisplay';
import './WhatsAppConnectorManager.css';

const { Option } = Select;
const { TextArea } = Input;

interface AgentsManagementProps {
  onDataChange?: () => void;
}

const AgentsManagement: React.FC<AgentsManagementProps> = ({ onDataChange }) => {
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [availableServices, setAvailableServices] = useState<any[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [form] = Form.useForm();
  const usageType = Form.useWatch('usage_type', form);

  // 加载数据
  const loadAgents = async () => {
    setLoading(true);
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      const response = await agentService.getAgents({ tenant_code: tenantCode });
      setAgents(response.data || []);
    } catch (error: any) {
      console.error('加载 Agents 失败:', error);
      message.error('加载 Agents 失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 加载可用的 AI Platform 服务
  const loadAvailableServices = async () => {
    try {
      const response = await agentService.getAvailableAIPlatformServices();
      if (response.success && response.data) {
        setAvailableServices(response.data);
      }
    } catch (error: any) {
      console.error('加载可用服务失败:', error);
    }
  };

  // 加载员工列表
  const loadEmployees = async () => {
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      const response = await employeeService.getEmployees(tenantCode);
      if (response.success && response.data) {
        // 只加载活跃的员工
        setEmployees(response.data.filter((emp: Employee) => emp.is_active === 1));
      }
    } catch (error: any) {
      console.error('加载员工列表失败:', error);
    }
  };

  useEffect(() => {
    loadAgents();
    loadAvailableServices();
    loadEmployees();
  }, []);

  // 表格列配置
  const columns = [
    {
      title: 'Agent 名称',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 200,
    },
    {
      title: 'Agent ID',
      dataIndex: 'agent_id',
      key: 'agent_id',
      width: 150,
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'agent_type',
      key: 'agent_type',
      width: 100,
      render: (type: string) => {
        const colorMap: Record<string, string> = {
          'chatbot': 'green',
          'assistant': 'blue',
          'workflow': 'purple'
        };
        return <Tag color={colorMap[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: '使用场景',
      dataIndex: 'usage_type',
      key: 'usage_type',
      width: 100,
      render: (usage: string) => (
        <Tag color={usage === 'employee' ? 'geekblue' : 'volcano'}>{usage || '-'}</Tag>
      ),
    },
    {
      title: '默认操作员',
      dataIndex: 'default_operator_employee_id',
      key: 'default_operator_employee_id',
      width: 150,
      render: (id: number, record: Agent) => {
        if (record.usage_type !== 'customer') return '-';
        if (!id) return '-';
        const employee = employees.find(emp => emp.id === id);
        return employee ? (
          <Tooltip title={`员工ID: ${employee.employee_id}`}>
            <Tag color="blue">{employee.employee_name}</Tag>
          </Tooltip>
        ) : <Tag color="default">ID: {id}</Tag>;
      },
    },
    {
      title: '关联服务',
      dataIndex: 'service_name',
      key: 'service_name',
      width: 150,
      render: (serviceName: string, record: Agent) => {
        if (!serviceName) return <Tag color="default">未关联</Tag>;
        return (
          <Tooltip title={`平台: ${record.platform_type || '未知'}`}>
            <Tag color="blue">{serviceName}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '平台类型',
      dataIndex: 'platform_type',
      key: 'platform_type',
      width: 120,
      render: (platform: string) => {
        if (!platform) return '-';
        const colorMap: Record<string, string> = {
          'openai': 'green',
          'claude': 'purple',
          'ibuild': 'blue',
          'dify': 'orange'
        };
        return <Tag color={colorMap[platform] || 'default'}>{platform}</Tag>;
      },
    },
    {
      title: '模型',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 120,
      render: (model: string) => model || '-',
    },
    {
      title: '处理模式',
      dataIndex: 'processing_mode',
      key: 'processing_mode',
      width: 100,
      render: (mode: string) => (
        <Tag color={mode === 'sync' ? 'green' : 'orange'}>{mode}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => (
        <Badge
          status={active ? 'success' : 'error'}
          text={active ? '活跃' : '停用'}
        />
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 100,
      render: (count: number) => count || 0,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Agent) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定删除这个 Agent 吗？"
              onConfirm={() => handleDelete(record.id!)}
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 处理函数
  const handleCreate = async (values: any) => {
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      const data: CreateAgentRequest = {
        tenant_code: tenantCode,
        ...values,
      };
      await agentService.createAgent(data);
      message.success('Agent 创建成功');
      setModalVisible(false);
      form.resetFields();
      loadAgents();
      onDataChange?.();
    } catch (error: any) {
      message.error('创建失败: ' + error.message);
    }
  };

  const handleEdit = (agent: Agent) => {
    setEditingAgent(agent);
    form.setFieldsValue(agent);
    setModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    if (!editingAgent) return;

    try {
      const data: UpdateAgentRequest = values;
      await agentService.updateAgent(editingAgent.id!, data);
      message.success('Agent 更新成功');
      setModalVisible(false);
      setEditingAgent(null);
      form.resetFields();
      loadAgents();
      onDataChange?.();
    } catch (error: any) {
      message.error('更新失败: ' + error.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await agentService.deleteAgent(id);
      message.success('Agent 删除成功');
      loadAgents();
      onDataChange?.();
    } catch (error: any) {
      message.error('删除失败: ' + error.message);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0 }}>Agents 管理</h3>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            管理 AI Agent 配置和参数
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingAgent(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          新建 Agent
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={agents}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          pageSize: 10,
        }}
        scroll={{ x: 1000 }}
      />

      {/* 新建/编辑模态框 */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            {editingAgent ? '编辑 Agent' : '新建 Agent'}
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAgent(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(vals) => {
            console.log('AgentsManagement submit values:', vals);
            return (editingAgent ? handleUpdate : handleCreate)(vals);
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agent_name"
                label="Agent 名称"
                rules={[{ required: true, message: '请输入 Agent 名称' }]}
              >
                <Input placeholder="请输入 Agent 名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="agent_id"
                label="Agent ID"
                rules={[{ required: true, message: '请输入 Agent ID' }]}
              >
                <Input placeholder="请输入 Agent ID" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="service_id"
                label="关联服务"
                rules={[{ required: true, message: '请选择关联的服务' }]}
              >
                <Select
                  placeholder="请选择 AI Platform 服务"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {availableServices.map(service => (
                    <Option key={service.id} value={service.id}>
                      {service.service_name} ({service.platform_type})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="agent_type"
                label="Agent 类型"
                rules={[{ required: true, message: '请选择 Agent 类型' }]}
              >
                <Select placeholder="请选择 Agent 类型">
                  <Option value="chatbot">Chatbot</Option>
                  <Option value="assistant">Assistant</Option>
                  <Option value="workflow">Workflow</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="model_name"
                label="模型名称"
              >
                <Input placeholder="请输入模型名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="usage_type"
                label="使用场景"
                initialValue="employee"
                tooltip="employee: 员工侧；customer: 客户侧"
                rules={[{ required: true, message: '请选择使用场景' }]}
              >
                <Select placeholder="请选择使用场景">
                  <Option value="employee">员工侧</Option>
                  <Option value="customer">客户侧</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {usageType === 'customer' && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="default_operator_employee_id"
                  label="默认操作员"
                  tooltip="仅客户侧生效：用于指定默认由哪个员工发送回复"
                  rules={[{ required: true, message: '请选择默认操作员' }]}
                >
                  <Select
                    placeholder="请选择员工"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {employees.map(emp => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.employee_name} ({emp.employee_id})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea
              rows={3}
              placeholder="请输入 Agent 描述"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="max_tokens"
                label="最大 Token 数"
              >
                <InputNumber
                  min={1}
                  max={100000}
                  placeholder="4000"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="temperature"
                label="Temperature"
              >
                <InputNumber
                  min={0}
                  max={2}
                  step={0.1}
                  placeholder="0.7"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="processing_mode"
                label="处理模式"
              >
                <Select placeholder="请选择处理模式">
                  <Option value="sync">同步</Option>
                  <Option value="async">异步</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="system_prompt"
            label="系统提示词"
          >
            <TextArea
              rows={4}
              placeholder="请输入系统提示词"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="timeout_seconds"
                label="超时时间（秒）"
              >
                <InputNumber
                  min={1}
                  max={300}
                  placeholder="30"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="max_retries"
                label="最大重试次数"
              >
                <InputNumber
                  min={0}
                  max={10}
                  placeholder="3"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_active"
                label="是否启用"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingAgent(null);
                  form.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingAgent ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentsManagement;
